import React, { useEffect } from "react";

import { AddProductPropsI } from "./App.types";
import { Overlay } from "./components/common/Overlay";
import { Drawer } from "./components/main/Drawer";
import { DrawerContent } from "./components/main/DrawerContent";
import { GeneralModal } from "./components/modals/GeneralModal";
import { MODAL_TYPES } from "./components/modals/GeneralModal.types";
import { getProductData, getSubscriptionData } from "./slices/actions";
import {
	setBehaviour,
	setIsGamePassAvailable,
	toggleIsVisible,
	toggleModal,
} from "./slices/popupSlice";
import {
	trackInteractionEvent,
	trackPopupViewEvent,
} from "./tracking/googleAnalytics";
import { getCookie } from "./utils/helpers/cookie";
import { sleep } from "./utils/helpers/sleep";
import {
	useDispatch,
	useSelector,
	useEventListener,
	useCachedStrings,
} from "./utils/hooks";

export const App = () => {
	const dispatch = useDispatch();
	const maxFullScreenWidth = 692;

	const togglePopup = (shouldBeHidden: boolean) => {
		dispatch(toggleIsVisible(!shouldBeHidden));
		document.body.style.overflow = shouldBeHidden ? "" : "hidden";
	};

	const onPopState = (event) => {
		if (event.state && event.state.popupOpen) {
			trackInteractionEvent({
				action: "PopupClosed",
				category: "CartPopup",
			});

			togglePopup(true);
		}
	};

	const onAddToCartPopupShow = async ({
		productId,
		renewalPeriod,
		isGamePassAvailable,
		isAddToCartDrawer,
		categoryId
	}: AddProductPropsI) => {
		if (getCookie("addToCartPopupConsent") === "False") return;
		if (window.innerWidth < maxFullScreenWidth) {
			history.replaceState({ popupOpen: true }, "", location.pathname);
			history.pushState(null, "", location.pathname);
		}

		dispatch(setBehaviour(isAddToCartDrawer));
		dispatch(setIsGamePassAvailable(isGamePassAvailable));

		await sleep(200);

		togglePopup(false);

		dispatch(
			getProductData({
				productId,
				renewalPeriod,
				isAddToCartDrawer,
				categoryId
			}),
		);

		trackPopupViewEvent({ category: "Product", name: "Add to cart" });

		document.body.style.overflow = "hidden";
	};

	const onSubscriptionDataPopupShow = async ({
		productId,
		price,
		taxRate,
		isConfigBundle,
		subGroup,
		renewalPeriod,
		isGamePassAvailable,
		isAddToCartDrawer,
		isConfigServiceIncluded,
		configServices,
	}: AddProductPropsI) => {
		if (getCookie("addToCartPopupConsent") === "False") return;
		if (window.innerWidth < maxFullScreenWidth) {
			history.replaceState({ popupOpen: true }, "", location.pathname);
			history.pushState(null, "", location.pathname);
		}

		dispatch(setBehaviour(isAddToCartDrawer));
		dispatch(setIsGamePassAvailable(isGamePassAvailable));

		await sleep(200);

		togglePopup(false);

		dispatch(
			getSubscriptionData({
				productId,
				price,
				taxRate,
				isConfigBundle,
				subGroup,
				renewalPeriod,
				isConfigServiceIncluded,
				configServices,
			}),
		);

		trackPopupViewEvent({ category: "Information", name: "Flex subscription" });

		document.body.style.overflow = "hidden";
	};

	const isVisible = useSelector((state) => state.popup.products.isVisible);
	const isModalOpen = useSelector((state) => state.popup.modal.isModalOpen);
	const modalId = useSelector((state) => state.popup.modal.id);
	const insurance = useSelector(
		(state) => state.popup.products.mainProduct.insurance,
	);

	useCachedStrings();

	useEffect(() => {
		if (isVisible || isModalOpen) {
			(document.querySelector("html") as HTMLElement).classList.add(
				"scroll-lock-xs",
			);

			trackInteractionEvent({
				action: "PopupShown",
				category: "CartPopup",
			});
			window.addEventListener("popstate", onPopState);
		}

		return () => {
			(document.querySelector("html") as HTMLElement).classList.remove(
				"scroll-lock-xs",
			);
			window.removeEventListener("popstate", onPopState);
		};
	}, [isVisible, isModalOpen]);

	useEventListener({
		type: "openAddToCartPopup",
		handler: onAddToCartPopupShow,
		withData: true,
	});

	useEventListener({
		type: "openSubscriptionDataPopup",
		handler: onSubscriptionDataPopupShow,
		withData: true,
	});

	return (
		<>
			<Overlay
				onOverlayHide={() => {
					togglePopup(true);
					if (
						modalId !== MODAL_TYPES.INSURANCE &&
						!isModalOpen &&
						insurance?.length
					) {
						dispatch(
							toggleModal({
								id: MODAL_TYPES.INSURANCE,
								isOpen: true,
							}),
						);
					}
				}}
			/>
			<Drawer>
				{isVisible && <DrawerContent closePopup={() => togglePopup(true)} />}
			</Drawer>
			<GeneralModal />
		</>
	);
};
